import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { authService } from '../services/authService';

interface User {
  user_id: string;
  tenant_id: string;
  email: string;
  org_slug?: string;
  role: string;
  permissions: string[];
  is_admin: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ requires_password_change?: boolean }>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Decode JWT token to check expiration
  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      
      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp) return true;
      
      // Check if token expires in less than 5 minutes (300 seconds)
      const expiresIn = payload.exp * 1000 - Date.now();
      return expiresIn < 300000; // 5 minutes buffer
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }, []);

  // Force logout when token expires
  const handleTokenExpiration = useCallback(() => {
    console.log('🔒 Token expired - logging out');
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('org_slug');
    authService.logout();
    window.location.href = '/login?reason=expired';
  }, []);

  // Reset activity timeout (30 minutes of inactivity)
  const resetActivityTimeout = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    // Set 30-minute inactivity timeout
    activityTimeoutRef.current = setTimeout(() => {
      console.log('🔒 Session expired due to inactivity');
      handleTokenExpiration();
    }, 30 * 60 * 1000); // 30 minutes
  }, [handleTokenExpiration]);

  // Track user activity
  useEffect(() => {
    if (!token) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      resetActivityTimeout();
    };

    // Set initial timeout
    resetActivityTimeout();

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [token, resetActivityTimeout]);

  // Periodic token validation (every 5 minutes)
  useEffect(() => {
    if (!token) return;

    const checkToken = async () => {
      try {
        if (isTokenExpired(token)) {
          handleTokenExpiration();
          return;
        }
        
        // Validate with server
        await authService.validateToken(token);
      } catch (error) {
        console.error('Token validation failed:', error);
        handleTokenExpiration();
      }
    };

    // Check immediately
    checkToken();

    // Check every 5 minutes
    tokenCheckIntervalRef.current = setInterval(checkToken, 5 * 60 * 1000);

    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
    };
  }, [token, isTokenExpired, handleTokenExpiration]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('auth_token');
        if (storedToken) {
          // Check if token is expired before validating
          if (isTokenExpired(storedToken)) {
            console.log('🔒 Stored token is expired');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('org_slug');
            setIsLoading(false);
            return;
          }

          const userData = await authService.validateToken(storedToken);
          setUser(userData);
          setToken(storedToken);
          // Store organization slug for org-scoped API endpoints
          if (userData.org_slug) {
            localStorage.setItem('org_slug', userData.org_slug);
          } else if (userData.tenant_id) {
            // Fallback to tenant_id if org_slug not available
            localStorage.setItem('org_slug', userData.tenant_id);
          }
        }
      } catch (error) {
        // Token is invalid, remove it
        localStorage.removeItem('auth_token');
        localStorage.removeItem('org_slug');
        console.error('Token validation failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isTokenExpired]);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await authService.login(email, password);
      
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('auth_token', response.token);
      // Store organization slug for org-scoped API endpoints
      if (response.user.org_slug) {
        localStorage.setItem('org_slug', response.user.org_slug);
      } else if (response.user.tenant_id) {
        // Fallback to tenant_id if org_slug not available
        localStorage.setItem('org_slug', response.user.tenant_id);
      }
      
      // Return requires_password_change flag
      return { requires_password_change: response.requires_password_change };
    } catch (error: any) {
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('org_slug');
    authService.logout();
    // Navigate to login page after logout
    // Use window.location.href to ensure full page reload and proper CloudFront routing
    window.location.href = '/login';
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    error,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
